import React, { useState, useMemo } from 'react';
import ProductCard from './ProductCard';
import ProductFilters, { FilterOptions } from './ProductFilters';
import ProductQuickView from './ProductQuickView';
import { Product, Collection, ProductVariant } from '../types';
import { SkeletonProductCard } from './common/Skeleton';

interface ProductsSectionProps {
  products: Product[];
  loading?: boolean;
  collections?: Collection[];
  loadingMore?: boolean;
  hasMore?: boolean;
  totalProducts?: number;
  onLoadMore?: () => void;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({
  products,
  loading = false,
  collections = [],
  loadingMore = false,
  hasMore = false,
  totalProducts,
  onLoadMore,
}) => {
  // Calculate min and max prices
  const { minPrice, maxPrice } = useMemo(() => {
    if (products.length === 0) return { minPrice: 0, maxPrice: 1000000 };
    const prices = products.map(p => p.discount_price || p.price);
    return {
      minPrice: Math.floor(Math.min(...prices) / 1000) * 1000,
      maxPrice: Math.ceil(Math.max(...prices) / 1000) * 1000,
    };
  }, [products]);

  // Get available sizes
  const availableSizes = useMemo(() => {
    const sizes = new Set<string>();
    products.forEach(p => {
      if (p.talla) {
        const tallaSizes = p.talla.split(',').map((s: string) => s.trim());
        tallaSizes.forEach((s: string) => sizes.add(s));
      }
      if (p.variants && Array.isArray(p.variants)) {
        p.variants.forEach((v: ProductVariant) => {
          if (v.talla) sizes.add(v.talla);
        });
      }
    });
    return Array.from(sizes).sort();
  }, [products]);

  // Filter state
  const [filters, setFilters] = useState<FilterOptions>({
    priceRange: [minPrice, maxPrice],
    collections: [],
    sizes: [],
    availability: 'all',
    sortBy: 'newest',
  });

  // Quick view state
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

  const handleQuickView = (product: Product) => {
    setQuickViewProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
    setTimeout(() => setQuickViewProduct(null), 300); // Clear after animation
  };

  const handleResetFilters = () => {
    setFilters({
      priceRange: [minPrice, maxPrice],
      collections: [],
      sizes: [],
      availability: 'all',
      sortBy: 'newest',
    });
  };

  // Apply filters and sorting
  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];

    // Filter by price
    result = result.filter(p => {
      const price = p.discount_price || p.price;
      return price >= filters.priceRange[0] && price <= filters.priceRange[1];
    });

    // Filter by collections
    if (filters.collections.length > 0) {
      result = result.filter(p =>
        p.collection_id && filters.collections.includes(p.collection_id)
      );
    }

    // Filter by sizes
    if (filters.sizes.length > 0) {
      result = result.filter(p => {
        if (p.talla) {
          const tallaSizes = p.talla.split(',').map((s: string) => s.trim());
          if (tallaSizes.some((s: string) => filters.sizes.includes(s))) return true;
        }
        if (p.variants && Array.isArray(p.variants)) {
          return p.variants.some((v: ProductVariant) =>
            v.talla && filters.sizes.includes(v.talla)
          );
        }
        return false;
      });
    }

    // Filter by availability
    result = result.filter(p => {
      let totalStock = 0;

      if (p.variants && Array.isArray(p.variants) && p.variants.length > 0) {
        totalStock = p.variants.reduce((sum: number, v: ProductVariant) => sum + (v.stock_quantity || 0), 0);
      } else {
        totalStock = p.stock_quantity;
      }

      if (filters.availability === 'in_stock') {
        return totalStock > 5;
      } else if (filters.availability === 'low_stock') {
        return totalStock > 0 && totalStock <= 5;
      }
      return totalStock > 0; // 'all' - only show products with stock
    });

    // Sort
    result.sort((a, b) => {
      switch (filters.sortBy) {
        case 'price_low':
          return (a.discount_price || a.price) - (b.discount_price || b.price);
        case 'price_high':
          return (b.discount_price || b.price) - (a.discount_price || a.price);
        case 'popularity':
          // Use is_featured as a proxy for popularity
          return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
    });

    return result;
  }, [products, filters]);

  return (
    <section id="shop" className="py-20 px-6 md:px-12 bg-canvas-secondary">
      <div className="max-w-container-content mx-auto">
        <h2 className="text-4xl md:text-6xl font-bold text-center mb-16 text-fg-primary uppercase tracking-widest">
          Productos Destacados
        </h2>

        {/* Filters */}
        {!loading && products.length > 0 && (
          <ProductFilters
            collections={collections}
            availableSizes={availableSizes}
            filters={filters}
            onFiltersChange={setFilters}
            onReset={handleResetFilters}
            minPrice={minPrice}
            maxPrice={maxPrice}
          />
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {Array.from({ length: 8 }).map((_, index) => (
              <SkeletonProductCard key={index} />
            ))}
          </div>
        ) : filteredAndSortedProducts.length === 0 ? (
          <div className="text-center py-20">
            <svg className="w-24 h-24 mx-auto mb-6 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="text-fg-secondary text-xl mb-4">No se encontraron productos</p>
            <p className="text-fg-tertiary text-sm mb-6">Intenta ajustar tus filtros</p>
            <button
              onClick={handleResetFilters}
              className="px-6 py-3 bg-fg-highlight-primary text-white rounded-lg font-medium hover:bg-opacity-90 transition-all duration-300"
            >
              Limpiar filtros
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6 text-center">
              <p className="text-fg-secondary text-sm">
                Mostrando {filteredAndSortedProducts.length} de {totalProducts ?? products.length} productos
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
              {filteredAndSortedProducts.map(product => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onQuickView={handleQuickView}
                />
              ))}
              {loadingMore && Array.from({ length: 4 }).map((_, index) => (
                <SkeletonProductCard key={`skeleton-${index}`} />
              ))}
            </div>
            {hasMore && onLoadMore && !loadingMore && (
              <div className="mt-12 text-center">
                <button
                  onClick={onLoadMore}
                  className="px-8 py-3 border-2 border-fg-primary text-fg-primary rounded-lg font-medium hover:bg-fg-primary hover:text-white transition-all duration-300 uppercase tracking-wider"
                >
                  Cargar más productos
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Quick View Modal */}
      <ProductQuickView
        product={quickViewProduct}
        isOpen={isQuickViewOpen}
        onClose={handleCloseQuickView}
      />
    </section>
  );
};

export default ProductsSection;
